import { useEffect, useState } from 'react';
import {
  checkGoogleConnection,
  disconnectGoogle,
  ensureGoogleSlidesEditorAccess,
  getGoogleConnection,
  getGooglePickerConfiguration,
  getGoogleSlidesExport,
  getPdfDelivery,
  listGoogleSlidesTemplates,
  registerGoogleSlidesTemplate,
  pdfDeliveryFileUrl,
  startGoogleOAuth,
  startGoogleSlidesExport,
  startPdfDelivery,
  type GoogleConnection,
  type GoogleSlidesExport,
  type GoogleSlidesTemplate,
  type PdfDelivery,
} from './api';
import { ElectronicDeliveryPanel } from './ElectronicDeliveryPanel';
import { VideoDeliveryPanel } from './VideoDeliveryPanel';

type PickerDocument = { id?: string; name?: string };
type PickerData = { action?: string; docs?: PickerDocument[] };
type PickerBuilder = {
  addView: (view: unknown) => PickerBuilder;
  build: () => { setVisible: (visible: boolean) => void };
  setAppId: (appId: string) => PickerBuilder;
  setCallback: (callback: (data: PickerData) => void) => PickerBuilder;
  setDeveloperKey: (key: string) => PickerBuilder;
  setOAuthToken: (token: string) => PickerBuilder;
};
type PickerApi = {
  Action: { PICKED: string };
  DocsView: new (viewId: string) => { setIncludeFolders: (include: boolean) => unknown };
  PickerBuilder: new () => PickerBuilder;
  ViewId: { PRESENTATIONS: string };
};

declare global {
  interface Window {
    gapi?: { load: (name: string, options: { callback: () => void; onerror: () => void }) => void };
    google?: { picker?: PickerApi };
  }
}

function wait(milliseconds: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
}

async function loadPicker(): Promise<PickerApi> {
  if (!window.gapi) {
    await new Promise<void>((resolve, reject) => {
      const existing = document.querySelector<HTMLScriptElement>('script[data-google-picker]');
      if (existing) {
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener(
          'error',
          () => reject(new Error('Google Pickerを読み込めませんでした。')),
          {
            once: true,
          },
        );
        return;
      }
      const script = document.createElement('script');
      script.dataset.googlePicker = 'true';
      script.src = 'https://apis.google.com/js/api.js';
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Google Pickerを読み込めませんでした。'));
      document.head.append(script);
    });
  }
  await new Promise<void>((resolve, reject) => {
    window.gapi?.load('picker', {
      callback: resolve,
      onerror: () => reject(new Error('Google Pickerを初期化できませんでした。')),
    });
  });
  const picker = window.google?.picker;
  if (!picker) throw new Error('Google Pickerを初期化できませんでした。');
  return picker;
}

export function GoogleSlidesPanel({
  approved,
  companyId,
  csrfToken,
  narrationText,
  onError,
  projectId,
  revisionId,
}: {
  approved: boolean;
  companyId: string;
  csrfToken: string;
  narrationText: string;
  onError: (error: unknown) => void;
  projectId: string;
  revisionId: string;
}) {
  const [connection, setConnection] = useState<GoogleConnection | null>(null);
  const [templates, setTemplates] = useState<GoogleSlidesTemplate[]>([]);
  const [templateId, setTemplateId] = useState('');
  const [paperTemplateId, setPaperTemplateId] = useState('');
  const [driveFileId, setDriveFileId] = useState('');
  const [templateName, setTemplateName] = useState('One Color 安全訓練テンプレート');
  const [templateType, setTemplateType] = useState<'PAPER_A4' | 'SCREEN_16_9'>('SCREEN_16_9');
  const [slideExport, setSlideExport] = useState<GoogleSlidesExport | null>(null);
  const [pdfDelivery, setPdfDelivery] = useState<PdfDelivery | null>(null);
  const [colorMode, setColorMode] = useState<'COLOR' | 'MONOCHROME'>('COLOR');
  const [sideMode, setSideMode] = useState<'DUPLEX' | 'SIMPLEX'>('SIMPLEX');
  const [includeNarration, setIncludeNarration] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [exportKey, setExportKey] = useState(() => crypto.randomUUID());
  const [pdfKey, setPdfKey] = useState(() => crypto.randomUUID());

  const screenTemplates = templates.filter((item) => item.templateType === 'SCREEN_16_9');
  const paperTemplates = templates.filter((item) => item.templateType === 'PAPER_A4');

  async function refresh(): Promise<void> {
    const nextConnection = await getGoogleConnection();
    setConnection(nextConnection);
    if (nextConnection.connected) {
      const [nextTemplates, nextExport, nextPdfDelivery] = await Promise.all([
        listGoogleSlidesTemplates(),
        getGoogleSlidesExport(companyId, projectId, revisionId),
        getPdfDelivery(companyId, projectId, revisionId),
      ]);
      setTemplates(nextTemplates);
      setTemplateId((current) =>
        nextTemplates.some((item) => item.id === current && item.templateType === 'SCREEN_16_9')
          ? current
          : (nextTemplates.find((item) => item.templateType === 'SCREEN_16_9')?.id ?? ''),
      );
      setPaperTemplateId((current) =>
        nextTemplates.some((item) => item.id === current && item.templateType === 'PAPER_A4')
          ? current
          : (nextTemplates.find((item) => item.templateType === 'PAPER_A4')?.id ?? ''),
      );
      setSlideExport(nextExport);
      setPdfDelivery(nextPdfDelivery);
    } else {
      setTemplates([]);
      setSlideExport(null);
      setPdfDelivery(null);
    }
  }

  useEffect(() => {
    let active = true;
    void Promise.all([
      getGoogleConnection(),
      getGoogleSlidesExport(companyId, projectId, revisionId).catch(() => null),
      getPdfDelivery(companyId, projectId, revisionId).catch(() => null),
    ])
      .then(async ([nextConnection, nextExport, nextPdfDelivery]) => {
        if (!active) return;
        setConnection(nextConnection);
        setSlideExport(nextExport);
        setPdfDelivery(nextPdfDelivery);
        if (nextConnection.connected) {
          const nextTemplates = await listGoogleSlidesTemplates();
          if (!active) return;
          setTemplates(nextTemplates);
          setTemplateId(
            nextTemplates.find((item) => item.templateType === 'SCREEN_16_9')?.id ?? '',
          );
          setPaperTemplateId(
            nextTemplates.find((item) => item.templateType === 'PAPER_A4')?.id ?? '',
          );
        }
      })
      .catch(onError);
    return () => {
      active = false;
    };
  }, [companyId, projectId, revisionId]);

  async function connect(): Promise<void> {
    setBusy(true);
    try {
      window.location.assign(await startGoogleOAuth(csrfToken));
    } catch (error) {
      onError(error);
      setBusy(false);
    }
  }

  async function check(): Promise<void> {
    setBusy(true);
    try {
      setConnection(await checkGoogleConnection(csrfToken));
      setNotice('Google接続を確認しました。');
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function pickTemplate(): Promise<void> {
    setBusy(true);
    try {
      const [picker, config] = await Promise.all([loadPicker(), getGooglePickerConfiguration()]);
      const view = new picker.DocsView(picker.ViewId.PRESENTATIONS);
      view.setIncludeFolders(false);
      new picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(config.accessToken)
        .setDeveloperKey(config.developerKey)
        .setAppId(config.appId)
        .setCallback((data) => {
          if (data.action === picker.Action.PICKED) {
            const document = data.docs?.[0];
            if (document?.id) setDriveFileId(document.id);
            if (document?.name) setTemplateName(document.name);
          }
        })
        .build()
        .setVisible(true);
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function registerTemplate(): Promise<void> {
    setBusy(true);
    try {
      const template = await registerGoogleSlidesTemplate(
        {
          driveFileId: driveFileId.trim(),
          name: templateName.trim(),
          templateType,
        },
        csrfToken,
      );
      const nextTemplates = await listGoogleSlidesTemplates();
      setTemplates(nextTemplates);
      if (template.templateType === 'PAPER_A4') setPaperTemplateId(template.id);
      else setTemplateId(template.id);
      setNotice(
        template.templateType === 'PAPER_A4'
          ? 'A4縦のページサイズとrevisionを確認して紙納品テンプレートを登録しました。'
          : 'テンプレートのプレースホルダーとrevisionを確認して登録しました。',
      );
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function generatePdfs(): Promise<void> {
    if (!paperTemplateId || slideExport?.status !== 'VERIFIED') return;
    setBusy(true);
    setNotice('A4紙納品版を組み立て、画面用・紙納品用PDFをR2へ保存しています。');
    try {
      let current = await startPdfDelivery(
        companyId,
        projectId,
        revisionId,
        { colorMode, includeNarration, paperTemplateId, sideMode },
        csrfToken,
        pdfKey,
      );
      setPdfDelivery(current);
      for (let attempt = 0; attempt < 90; attempt += 1) {
        if (current.status === 'VERIFIED' || current.status === 'FAILED') break;
        await wait(2000);
        current = (await getPdfDelivery(companyId, projectId, revisionId)) ?? current;
        setPdfDelivery(current);
        setNotice(`PDF・紙納品資料を生成しています（${(attempt + 1) * 2}秒経過）…`);
      }
      if (current.status === 'VERIFIED') {
        setNotice('画面用PDFとA4紙納品用PDFを検証し、R2へ非公開保存しました。');
        setPdfKey(crypto.randomUUID());
      } else if (current.status === 'FAILED') {
        setNotice(current.errorDetail ?? 'PDF生成に失敗しました。途中工程から再実行できます。');
      } else {
        setNotice('PDF生成処理を続行しています。「Google状態を更新」で確認できます。');
      }
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function generate(): Promise<void> {
    if (!templateId) return;
    setBusy(true);
    setNotice('テンプレートを複製し、本文・図解・speaker notesを反映しています。');
    try {
      let current = await startGoogleSlidesExport(
        companyId,
        projectId,
        revisionId,
        templateId,
        csrfToken,
        exportKey,
      );
      setSlideExport(current);
      for (let attempt = 0; attempt < 45; attempt += 1) {
        if (current.status === 'VERIFIED' || current.status === 'FAILED') break;
        await wait(2000);
        current = (await getGoogleSlidesExport(companyId, projectId, revisionId)) ?? current;
        setSlideExport(current);
        setNotice(`Google Slidesを生成・検証しています（${(attempt + 1) * 2}秒経過）…`);
      }
      if (current.status === 'VERIFIED') {
        setNotice(
          'Google Slidesを生成し、プレースホルダー0件・speaker notes一致・操作中の管理者の編集権限を確認しました。',
        );
        setExportKey(crypto.randomUUID());
      } else if (current.status === 'FAILED') {
        setNotice(current.errorDetail ?? 'Google Slides生成に失敗しました。再実行できます。');
      } else {
        setNotice('生成処理を続行しています。「Google状態を更新」で確認できます。');
      }
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  async function repairEditorAccess(): Promise<void> {
    if (
      !window.confirm(
        '現在ログイン中のOne Color管理者を、このGoogle Slidesの編集者に設定します。実行しますか？',
      )
    )
      return;
    setBusy(true);
    try {
      const result = await ensureGoogleSlidesEditorAccess(
        companyId,
        projectId,
        revisionId,
        csrfToken,
      );
      setNotice(
        result.status === 'ADDED' || result.status === 'UPGRADED'
          ? `${result.email} に編集権限を設定しました。`
          : `${result.email} の編集権限は設定済みです。`,
      );
    } catch (error) {
      onError(error);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="google-slides-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">GOOGLE SLIDES DELIVERY</p>
          <h4>Google Slides生成</h4>
        </div>
        <button
          className="secondary"
          disabled={busy}
          onClick={() => void refresh().catch(onError)}
          type="button"
        >
          Google状態を更新
        </button>
      </div>

      {notice !== null && <div className="notice warning">{notice}</div>}
      {connection === null ? (
        <div className="material-loading">Google接続を確認しています…</div>
      ) : !connection.configured ? (
        <div className="notice warning">
          Google OAuthが未設定です。管理者がClient ID、Client Secret、redirect
          URI、暗号化鍵をSecretsへ設定してください。
        </div>
      ) : !connection.connected ? (
        <div className="connection-card">
          <p>
            Google DriveとSlidesへ接続してください。Driveは選択・作成したファイルに限定されます。
            Slidesはプレゼンテーションの生成・更新・検証に使用します。
          </p>
          <button disabled={busy} onClick={() => void connect()} type="button">
            Googleへ接続
          </button>
        </div>
      ) : (
        <>
          <div className="connection-card connected">
            <span>
              <strong>Googleファイル作成アカウント</strong>
              <small>
                {connection.accountEmail ?? connection.accountName ?? 'Googleアカウント'}
              </small>
            </span>
            <button
              className="secondary"
              disabled={busy}
              onClick={() => void check()}
              type="button"
            >
              接続確認
            </button>
            <button
              className="secondary"
              disabled={busy}
              onClick={() => void connect()}
              type="button"
            >
              作成アカウントを変更
            </button>
            <button
              className="danger ghost"
              disabled={busy}
              onClick={() => {
                if (!window.confirm('Googleとの接続を解除します。生成済みSlidesは削除されません。'))
                  return;
                setBusy(true);
                void disconnectGoogle('One Color管理者によるGoogle接続解除', csrfToken)
                  .then(refresh)
                  .catch(onError)
                  .finally(() => setBusy(false));
              }}
              type="button"
            >
              接続解除
            </button>
          </div>
          <p className="muted">
            このアカウントがGoogle Slidesの所有・編集と、Google Vids／Geminiの手動作業に使われます。
            公開サポートメールとは別に設定できます。切替は新しい認証が完了した時点で反映されます。
          </p>

          <details className="template-registration" open={templates.length === 0}>
            <summary>Slidesテンプレート登録</summary>
            <p className="muted">
              元テンプレートは変更しません。登録時に必須プレースホルダーとrevisionを検査します。
            </p>
            <button
              className="secondary"
              disabled={busy}
              onClick={() => void pickTemplate()}
              type="button"
            >
              Google Pickerから選択
            </button>
            <label>
              テンプレート種別
              <select
                onChange={(event) => {
                  const next = event.target.value as 'PAPER_A4' | 'SCREEN_16_9';
                  setTemplateType(next);
                  setTemplateName(
                    next === 'PAPER_A4'
                      ? 'One Color 紙納品 A4テンプレート'
                      : 'One Color 安全訓練テンプレート',
                  );
                }}
                value={templateType}
              >
                <option value="SCREEN_16_9">画面用 16:9</option>
                <option value="PAPER_A4">紙納品用 A4縦</option>
              </select>
            </label>
            <label>
              テンプレート名
              <input
                onChange={(event) => setTemplateName(event.target.value)}
                value={templateName}
              />
            </label>
            <label>
              Google SlidesファイルID
              <input onChange={(event) => setDriveFileId(event.target.value)} value={driveFileId} />
            </label>
            <button
              disabled={busy || driveFileId.trim().length < 10 || templateName.trim().length === 0}
              onClick={() => void registerTemplate()}
              type="button"
            >
              preflightして登録
            </button>
          </details>

          <label>
            使用テンプレート
            <select onChange={(event) => setTemplateId(event.target.value)} value={templateId}>
              <option value="">テンプレートを選択</option>
              {screenTemplates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}（placeholder {template.placeholderCount}種）
                </option>
              ))}
            </select>
          </label>

          {!approved && (
            <div className="notice warning">DRAFT内容を承認するとSlides生成を開始できます。</div>
          )}
          <button
            disabled={busy || !approved || templateId === ''}
            onClick={() => void generate()}
            type="button"
          >
            {slideExport?.status === 'FAILED' ? '途中工程から再実行' : 'Google Slidesを生成'}
          </button>

          {slideExport !== null && (
            <div className={`slides-export-status ${slideExport.status.toLowerCase()}`}>
              <strong>生成状態: {slideExport.status}</strong>
              {slideExport.errorCode !== null && <span>{slideExport.errorCode}</span>}
              {slideExport.errorDetail !== null && <p>{slideExport.errorDetail}</p>}
              {slideExport.status === 'VERIFIED' && slideExport.driveWebUrl !== null && (
                <>
                  <a href={slideExport.driveWebUrl} rel="noreferrer" target="_blank">
                    生成したGoogle Slidesを開く
                  </a>
                  <p className="muted">
                    生成を実行したOne Color管理者には編集権限を自動設定します。Google SlidesはOne
                    Color内部確認用で、一般公開や契約企業への直接共有は行いません。契約企業には個人Googleアドレスを表示せず、最終確認・公開後に専用納品ページからPDF等を提供します。
                  </p>
                  <button
                    className="secondary"
                    disabled={busy}
                    onClick={() => void repairEditorAccess()}
                    type="button"
                  >
                    自分の編集権限を確認・修復
                  </button>
                </>
              )}
            </div>
          )}

          {slideExport?.status === 'VERIFIED' && (
            <section className="pdf-delivery-panel">
              <h4>PDF・紙納品資料</h4>
              {paperTemplates.length === 0 ? (
                <div className="notice warning">
                  A4縦のGoogle Slidesを「紙納品用
                  A4縦」として登録してください。元ファイルは変更しません。
                </div>
              ) : (
                <>
                  <label>
                    紙納品テンプレート
                    <select
                      onChange={(event) => setPaperTemplateId(event.target.value)}
                      value={paperTemplateId}
                    >
                      <option value="">テンプレートを選択</option>
                      {paperTemplates.map((template) => (
                        <option key={template.id} value={template.id}>
                          {template.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    印刷色
                    <select
                      onChange={(event) =>
                        setColorMode(event.target.value as 'COLOR' | 'MONOCHROME')
                      }
                      value={colorMode}
                    >
                      <option value="COLOR">カラー</option>
                      <option value="MONOCHROME">モノクロ</option>
                    </select>
                  </label>
                  <label>
                    印刷面
                    <select
                      onChange={(event) => setSideMode(event.target.value as 'DUPLEX' | 'SIMPLEX')}
                      value={sideMode}
                    >
                      <option value="SIMPLEX">片面</option>
                      <option value="DUPLEX">両面（章を表面から開始）</option>
                    </select>
                  </label>
                  <label className="checkbox-line">
                    <input
                      checked={includeNarration}
                      onChange={(event) => setIncludeNarration(event.target.checked)}
                      type="checkbox"
                    />
                    ナレーション原稿も紙面へ添付する
                  </label>
                  <button
                    disabled={busy || paperTemplateId === ''}
                    onClick={() => void generatePdfs()}
                    type="button"
                  >
                    {pdfDelivery?.status === 'FAILED'
                      ? 'PDF工程を途中から再実行'
                      : 'PDF・紙納品資料を生成'}
                  </button>
                </>
              )}
              {pdfDelivery !== null && (
                <div className={`slides-export-status ${pdfDelivery.status.toLowerCase()}`}>
                  <strong>PDF生成状態: {pdfDelivery.status}</strong>
                  {pdfDelivery.errorDetail !== null && <p>{pdfDelivery.errorDetail}</p>}
                  {pdfDelivery.paperDeckUrl !== null && (
                    <a href={pdfDelivery.paperDeckUrl} rel="noreferrer" target="_blank">
                      A4紙納品版Google Slidesを確認
                    </a>
                  )}
                  {pdfDelivery.artifacts.map((artifact) => (
                    <div key={artifact.artifactType}>
                      <span>
                        {artifact.displayName}：{artifact.status}
                        {artifact.pageCount === null ? '' : `（${artifact.pageCount}ページ）`}
                      </span>
                      {artifact.fileId !== null && artifact.status === 'VERIFIED' && (
                        <a
                          href={pdfDeliveryFileUrl(
                            companyId,
                            projectId,
                            revisionId,
                            artifact.fileId,
                          )}
                          rel="noreferrer"
                          target="_blank"
                        >
                          PDFを確認
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
          {pdfDelivery?.status === 'VERIFIED' && (
            <>
              <ElectronicDeliveryPanel
                companyId={companyId}
                csrfToken={csrfToken}
                onError={onError}
                projectId={projectId}
                revisionId={revisionId}
              />
              <VideoDeliveryPanel
                companyId={companyId}
                csrfToken={csrfToken}
                narrationText={narrationText}
                onError={onError}
                projectId={projectId}
                revisionId={revisionId}
              />
            </>
          )}
        </>
      )}
    </section>
  );
}
